import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { GenericGet } from '@/_shared/types';
import { GenericApiError } from '@/_shared/types/apiError';
import { getCurrentOrganization } from '@/core/state';
import { API_ORGANIZATIONS_ORGANIZATIONID_SURVEYS_CAMPAIGNS_RESULTS_STATS_PATH_KEY } from '@/swagger/constants/surveys-keys';
import {
  API_GET_SURVEY_CAMPAIGNS_RESULTS_STATS,
  KEY_GET_SURVEY_CAMPAIGNS_RESULTS_STATS,
} from '../constats';
import { MOCK_RESPONSE_GET_SURVEY_CAMPAIGNS_RESULTS_STATS } from '../mocks';

type RequestQueryGetSurveyCampaignsResultsStats = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_SURVEYS_CAMPAIGNS_RESULTS_STATS_PATH_KEY
>['request']['query'];

type RequestGetSurveyCampaignsResultsStats = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_SURVEYS_CAMPAIGNS_RESULTS_STATS_PATH_KEY
>['request']['path'];

export type ResponseGetSurveyCampaignsResultsStats = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_SURVEYS_CAMPAIGNS_RESULTS_STATS_PATH_KEY
>['response']['body'];

type CallParamsGetSurveyCampaignsResultsStats = RequestQueryGetSurveyCampaignsResultsStats &
  Omit<RequestGetSurveyCampaignsResultsStats, 'organizationId'> & {
    successSaga?: CallbackSaga;
    errorSaga?: CallbackSaga;
  };

export type CallResponseGetSurveyCampaignsResultsStats = ResponseGetSurveyCampaignsResultsStats;

type CallErrorGetSurveyCampaignsResultsStats = GenericApiError;

type CallSelectorsGetSurveyCampaignsResultsStats = {
  hasMoreThanOneSurveyCampaignTemplate: boolean;
  departmentCampaignsCount: number;
  teamCampaignsCount: number;
};

export const getSurveyCampaignsResultsStatsCall = createGeneralCall<
  CallParamsGetSurveyCampaignsResultsStats,
  CallResponseGetSurveyCampaignsResultsStats,
  CallErrorGetSurveyCampaignsResultsStats,
  CallSelectorsGetSurveyCampaignsResultsStats
>({
  id: KEY_GET_SURVEY_CAMPAIGNS_RESULTS_STATS,
  method: 'get',
  url:
    ({ payload }) =>
    state => {
      const org = getCurrentOrganization(state);
      return API_GET_SURVEY_CAMPAIGNS_RESULTS_STATS(org, payload);
    },
  successMock: IS_MOCK_ENABLED({ avoidMockOnDemoMode: true })
    ? MOCK_RESPONSE_GET_SURVEY_CAMPAIGNS_RESULTS_STATS
    : undefined,
  selectors: {
    departmentCampaignsCount: ({ selectors }) =>
      createSelector(selectors.response, res => {
        if (res && res.length > 0) {
          return res.filter(
            item =>
              item.stats?.departmentsStats &&
              item.stats.departmentsStats[0] &&
              item.stats.departmentsStats[0]?.totalInvited !== undefined &&
              item.stats.departmentsStats[0]?.totalInvited > 0,
          ).length;
        }
        return 0;
      }),
    teamCampaignsCount: ({ selectors }) =>
      createSelector(selectors.response, res => {
        if (res && res.length > 0) {
          return res.filter(
            item =>
              item.stats?.teamStats &&
              item.stats.teamStats.totalInvited !== undefined &&
              item.stats.teamStats.totalInvited > 0,
          ).length;
        }
        return 0;
      }),
    hasMoreThanOneSurveyCampaignTemplate: ({ selectors }) =>
      createSelector(selectors.response, res => {
        if (res && res.length > 0) {
          const getOnlyCampaignWithData = res.filter(
            item =>
              item.stats?.departmentsStats &&
              item.stats.departmentsStats[0] &&
              item.stats.departmentsStats[0]?.totalInvited !== undefined &&
              item.stats.departmentsStats[0]?.totalInvited > 0,
          );
          const uniqueTemplates = getOnlyCampaignWithData
            ?.map(item => item.surveyTemplateId)
            .filter((value, index, self) => self.indexOf(value) === index);
          return uniqueTemplates.length > 1;
        }
        return false;
      }),
  },
});
